import { useEffect, useState } from 'react';
import { notification } from 'antd';
import axios from './axiosInstances/privateAxiosInstance';
import {
  ProxyCredential,
  UserCredential,
  UseFetchProxyCredentialResponse,
  UseFetchUserCredentialResponse,
  ServiceListResponse,
} from 'src/types/ProxyServiceInfo';

export const useProxyServiceCreate = (proxyService: ProxyCredential | null, onCreate: () => void) => {
  useEffect(() => {
    const createProxyService = async () => {
      if (proxyService) {
        try {
          await axios.post('/qs/anonadmin/addProxyService', proxyService);
          console.log('Proxy service created successfully');
          onCreate();
          notification.success({
            message: 'Success',
            description: 'Proxy service added successfully',
          });
        } catch (error) {
          console.error('Error while creating proxy service', error);
          notification.error({
            message: 'Duplicates are not allowed.',
          });
        }
      }
    };

    createProxyService();
  }, [proxyService]);

  return [];
};

export const useFetchUserCredential = (proxyId: number): UseFetchUserCredentialResponse => {
  const [userCredential, setUserCredential] = useState<UserCredential | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userCredentialData = async () => {
      try {
        const response: UserCredential = await axios.get('/qs/anonadmin/getEditUserCredential', {
          params: {
            proxyId,
          },
        });

        setUserCredential(response);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    userCredentialData();
  }, [proxyId]);

  return { loading, userCredential };
};

export const useUserDetailsUpadate = (userDetailsUpdate: ProxyCredential | null, onUpdate: () => void) => {
  useEffect(() => {
    const updateUserDetails = async () => {
      if (userDetailsUpdate) {
        try {
          await axios.put('/qs/anonadmin/editUserCredential', userDetailsUpdate);
          onUpdate();
          notification.success({
            message: 'Success',
            description: `User details updated successfully with Proxy Id: ${userDetailsUpdate.proxyId}`,
          });
        } catch (error) {
          console.error(error);
          notification.error({
            message: 'Duplicates are not allowed.',
          });
        }
      }
    };

    updateUserDetails();
  }, [userDetailsUpdate]);

  return [];
};

export const useFetchProxyCredential = (proxyId: number): UseFetchProxyCredentialResponse => {
  const [proxyCredential, setProxyCredential] = useState<ProxyCredential | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const proxyCredentialData = async () => {
      try {
        const response: ProxyCredential = await axios.get('/qs/anonadmin/getProxyEditData', {
          params: {
            proxyId,
          },
        });

        setProxyCredential(response);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    proxyCredentialData();
  }, [proxyId]);

  return { loading, proxyCredential };
};

export const useProxyDetailsUpadate = async (proxyDetailsUpdate: ProxyCredential | null, onUpdate: () => void) => {
  useEffect(() => {
    const updateProxyDetails = async () => {
      if (proxyDetailsUpdate) {
        try {
          axios && (await axios.put('/qs/anonadmin/editProxyConfiguration', proxyDetailsUpdate));

          onUpdate();
          notification.success({
            message: 'Success',
            description: `Proxy details updated successfully with Proxy Id: ${proxyDetailsUpdate.proxyId}`,
          });
        } catch (error) {
          console.error(error);
          notification.error({
            message: 'Duplicates are not allowed.',
          });
        }
      }
    };

    updateProxyDetails();
  }, [proxyDetailsUpdate]);

  return [];
};

export const useGetServiceList = (params: ProxyCredential) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [proxyServiceList, setProxyServiceList] = useState<ProxyCredential[]>([]);

  useEffect(() => {
    const getServiceList = async () => {
      try {
        if (params) {
          setLoading(true);
          const response: ServiceListResponse = await axios.get('/qs/anonadmin/getproxyservicelist', {
            params: params,
          });
          if (response) {
            setProxyServiceList(response.proxyServiceList);
            setTotalRecords(response.totalRecords);
          }
          setLoading(false);
        } else {
          setProxyServiceList([]);
          setTotalRecords(0);
        }
      } catch (error) {
        setLoading(false);
      }
    };
    getServiceList();
  }, [params]);

  return { loading, totalRecords, proxyServiceList };
};
